import {
  Box,
  Button,
  createStyles,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Typography,
  Chip,
  useTheme,
  useMediaQuery,
  Modal,
  Slide,
  IconButton,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TableSortLabel,
} from "@material-ui/core";
import BACKGROUND from "assets/img/bg_manager_student.png";
import { toast } from "react-toastify";
import { withStyles } from "@material-ui/styles";
import React, { useEffect, useRef, useState } from "react";
import { Pagination } from "@material-ui/lab";
import { useTranslation } from "react-i18next";
import useStyles from "./style";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { ListParams, ResponseStatus } from "models/common";
import {
  selectFilterInreviewUserList,
  fetchUserList,
  selectUserList,
  setFilterInreviewUserList,
} from "features/Admin/pages/ManagerAdmin/managerAdminSlice";
import managerUserApi from "api/managerUser";
import { useConfirm } from "material-ui-confirm";
import EmptyComponent from "components/Empty";
import CloseIcon from "@material-ui/icons/Close";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import InputSearch from "components/InputSearch";
import clsx from "clsx";
import { dateTimeToFormatDDMMYYYY, dateTimeToFormatHHMM } from "utils/dateTimeFormat";

export interface RegistrationListProps { }
export default function RegistrationList(props: RegistrationListProps) {
  const StyledTableCell = withStyles((theme: Theme) =>
    createStyles({
      head: {
        padding: "16px 5px",
        color: "#1B1B1B",
        fontSize: 11,
        fontWeight: 700,
        borderRight: "1px solid rgba(224, 224, 224, 1)",
        borderBottom: "1px solid #777777",
        top: "0 !important",
        [theme.breakpoints?.up("xl")]: {
          fontSize: 13,
        },
      },
      body: {
        padding: "16px 5px",
        fontSize: 11,
        borderRight: "1px solid rgba(224, 224, 224, 0.8)",
        [theme.breakpoints?.up("xl")]: {
          fontSize: 13,
        },
      },
    })
  )(TableCell);

  const { t } = useTranslation();
  const classes = useStyles({
    background: BACKGROUND,
  });
  const confirm = useConfirm();
  const filter = useAppSelector(selectFilterInreviewUserList);
  const dispatch = useAppDispatch();
  const userList = useAppSelector(selectUserList);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const containerRef = useRef<HTMLDivElement>(null);

  const [selectedUser, setSelectedUser] = useState<any>(null);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [isSticky, setIsSticky] = useState(false);
  const [sortType, setSortType] = useState<number>(2); // 1 = Name, 2 = Date
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");

  useEffect(() => {
    const scrollContainer = containerRef.current?.closest("main");
    if (!scrollContainer) return;

    const handleScroll = () => {
      if (scrollContainer.scrollTop > 120) {
        setIsSticky(true);
      } else {
        setIsSticky(false);
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleOpenDetailModal = async (user: any) => {
    setSelectedUser(user);
    setOpenDetailModal(true);
    try {
      const fullDetail = await managerUserApi.detailUser(user.id);
      if (fullDetail) {
        setSelectedUser((prev: any) => ({ ...prev, ...fullDetail }));
      }
    } catch (e) {
      console.error("Failed to fetch full user detail", e);
    }
  };

  const handleCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectedUser(null);
  };

  const handleSearchChange = (newFilter: ListParams) => {
    dispatch(
      setFilterInreviewUserList({
        ...filter,
        page: 0,
        name: newFilter.name,
        name_like: newFilter.name_like,
      })
    );
  };

  const handleSortType = (type: number) => {
    setSortType(type);
    dispatch(
      setFilterInreviewUserList({
        ...filter,
        page: 0,
        sortType: type,
        sortDirection,
      })
    );
  };

  const handleSortHeader = (type: number) => {
    if (sortType === type) {
      const nextDir = sortDirection === "asc" ? "desc" : "asc";
      setSortDirection(nextDir);
      dispatch(
        setFilterInreviewUserList({
          ...filter,
          page: 0,
          sortType: type,
          sortDirection: nextDir,
        })
      );
    } else {
      setSortType(type);
      setSortDirection("desc");
      dispatch(
        setFilterInreviewUserList({
          ...filter,
          page: 0,
          sortType: type,
          sortDirection: "desc",
        })
      );
    }
  };

  const toggleSortDirection = () => {
    const nextDir = sortDirection === "asc" ? "desc" : "asc";
    setSortDirection(nextDir);
    dispatch(
      setFilterInreviewUserList({
        ...filter,
        page: 0,
        sortDirection: nextDir,
      })
    );
  };

  const sortedContent = React.useMemo(() => {
    if (!userList?.content || !Array.isArray(userList.content)) return [];
    const list = [...userList.content];

    return list.sort((a, b) => {
      if (sortType === 1) {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();
        return sortDirection === "asc"
          ? nameA.localeCompare(nameB, "vi", { sensitivity: "base" })
          : nameB.localeCompare(nameA, "vi", { sensitivity: "base" });
      } else {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return sortDirection === "asc" ? dateA - dateB : dateB - dateA;
      }
    });
  }, [userList?.content, sortType, sortDirection]);

  useEffect(() => {
    const targetParents: HTMLElement[] = [];
    const originalOverflows: string[] = [];

    if (containerRef.current) {
      let parent = containerRef.current.parentElement;
      while (parent && parent.tagName !== "MAIN") {
        const computedStyle = window.getComputedStyle(parent);
        if (
          computedStyle.overflow === "hidden" ||
          computedStyle.overflowX === "hidden" ||
          computedStyle.overflowY === "hidden"
        ) {
          targetParents.push(parent);
          originalOverflows.push(parent.style.overflow);
          parent.style.overflow = "visible";
        }
        parent = parent.parentElement;
      }
    }

    return () => {
      targetParents.forEach((parent, index) => {
        if (parent) {
          parent.style.overflow = originalOverflows[index];
        }
      });
    };
  }, [userList]);

  useEffect(() => {
    dispatch(fetchUserList(filter));
  }, [dispatch, filter]);

  const handleApproved = async (id: any) => {
    let params = {
      user_id_list: [id],
      action: "Approved",
    };
    try {
      const res: ResponseStatus = await managerUserApi.approvedUser(params);
      if (res.responseCode === "00") {
        toast.success(`${t("managerStudent.duyethocvienthanhcong")}`);
        dispatch(fetchUserList(filter));
      }
    } catch (error) { }
  };

  const handleChangePage = (page: number) => {
    dispatch(
      setFilterInreviewUserList({
        ...filter,
        page: page - 1,
      })
    );
  };

  const handleDelete = async (user: any) => {
    confirm({
      title: (
        <Typography variant="h5" color="secondary">
          {t("delete.chuy")}
        </Typography>
      ),
      description: (
        <Typography variant="subtitle2">
          {t("delete.khiban")}{" "}
          <Typography variant="subtitle2" component="span" color="secondary">
            {t("delete.dongy")}
          </Typography>{" "}
          {t("delete.xoa")}
          <Typography variant="subtitle2" component="span" style={{ color: "#004673" }}>
            {" "}
            {user.name}
          </Typography>{" "}
          {t("delete.sexoatatcadulieu")}
        </Typography>
      ),
      confirmationText: <Button className={classes.btn_delete}>{t("delete.dongy")}</Button>,
      cancellationText: (
        <Button className={classes.btn_edit} style={{ margin: 0 }}>
          {t("delete.huybo")}
        </Button>
      ),
    }).then(async () => {
      try {
        const res: ResponseStatus = await managerUserApi.deleteUsers(user.id);

        if (res.responseCode === "00") {
          toast.success(`${t("managerStudent.xoahocvienthanhcong")}`);
          dispatch(fetchUserList(filter));
        }
      } catch (error) { }
    });
  };

  return (
    <div ref={containerRef} className={classes.root}>
      <Box className={classes.background}>
        <Typography variant="subtitle2"> {t("admin.listitem.danhsachdangky")}</Typography>
      </Box>

      {/* Sticky Filter & Sort Toolbar */}
      <Box className={clsx(classes.filterToolbar, isSticky && classes.stickyActive)}>
        <Box style={{ width: isMobile ? "100%" : 320 }}>
          <InputSearch
            filter={filter}
            onSearchChange={handleSearchChange}
            label={t("managerStudent.timkiemhocvientheoten")}
          />
        </Box>

        <Box className={classes.sortGroup}>
          <FormControl variant="outlined" size="small" style={{ minWidth: 170 }}>
            <Select
              value={sortType}
              onChange={(e: any) => handleSortType(Number(e.target.value))}
              style={{ fontSize: "0.875rem", fontWeight: 700 }}
            >
              <MenuItem value={2}>Sắp xếp theo ngày</MenuItem>
              <MenuItem value={1}>Sắp xếp theo tên</MenuItem>
            </Select>
          </FormControl>

          <Button
            startIcon={sortDirection === "asc" ? <ArrowUpwardIcon fontSize="small" /> : <ArrowDownwardIcon fontSize="small" />}
            onClick={toggleSortDirection}
            className={classes.btnSortDirection}
          >
            {sortDirection === "asc" ? "Tăng dần" : "Giảm dần"}
          </Button>
        </Box>
      </Box>

      {userList.totalElements > 0 ? (
        <>
          <TableContainer style={{ margin: "20px 0" }} className={classes.container} component={Paper}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <StyledTableCell align="center" width="20px">
                    {t("managerStudent.register.stt")}
                  </StyledTableCell>
                  <StyledTableCell align="left" width="110px">
                    <TableSortLabel
                      active={sortType === 2}
                      direction={sortDirection}
                      onClick={() => handleSortHeader(2)}
                    >
                      {t("managerStudent.register.ngaydangky")}
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell align="left" width="180px">
                    <TableSortLabel
                      active={sortType === 1}
                      direction={sortDirection}
                      onClick={() => handleSortHeader(1)}
                    >
                      Học viên
                    </TableSortLabel>
                  </StyledTableCell>
                  <StyledTableCell align="left" width="90px">
                    {t("managerStudent.register.manhanvien")}
                  </StyledTableCell>
                  <StyledTableCell align="left" width="120px">
                    {t("managerStudent.register.class")}
                  </StyledTableCell>
                  <StyledTableCell align="center" width="200px">
                    {t("managerStudent.register.thaotac")}
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedContent.map((user, index) => (
                  <TableRow key={index} hover onClick={() => handleOpenDetailModal(user)} style={{ cursor: "pointer" }}>
                    <StyledTableCell align="center" component="th" scope="user">
                      {userList.size * userList.number + index + 1}
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      <Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", paddingLeft: 8 }}>
                        <Typography style={{ fontWeight: 700, color: "#0F172A", fontSize: "0.8125rem" }}>
                          {user?.createdAt ? dateTimeToFormatDDMMYYYY(user.createdAt) : "---"}
                        </Typography>
                        <Typography style={{ color: "#64748B", fontSize: "0.75rem", fontWeight: 500, marginTop: 2 }}>
                          {user?.createdAt ? dateTimeToFormatHHMM(user.createdAt) : ""}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="left">
                      <Box style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", paddingLeft: 8 }}>
                        <Typography style={{ fontWeight: 800, color: "#0F172A", fontSize: "0.85rem" }}>
                          {user?.name || "---"}
                        </Typography>
                        <Typography style={{ color: "#64748B", fontSize: "0.75rem", fontWeight: 500 }}>
                          {user?.email || "---"}
                        </Typography>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell align="left" style={{ paddingLeft: 14 }}>{user?.staffId || "---"}</StyledTableCell>
                    <StyledTableCell align="left" style={{ paddingLeft: 14 }}>{user?.class?.name || "Chưa xếp lớp"}</StyledTableCell>
                    <StyledTableCell align="center" onClick={(e: any) => e.stopPropagation()}>
                      <Box style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center" }}>
                        <Button className={classes.btn_detail} onClick={() => handleApproved(user.id)}>
                          {t("button.duyet")}
                        </Button>
                        <Button className={classes.btn_delete} onClick={() => handleDelete(user)}>
                          {t("button.delete")}
                        </Button>
                      </Box>
                    </StyledTableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Mobile View: ListView */}
          {isMobile && (
            <Box className={classes.mobileListView}>
              {sortedContent.map((user, index) => (
                <Box key={user.id} className={classes.mobileCard} onClick={() => handleOpenDetailModal(user)} style={{ cursor: "pointer" }}>
                  {/* Card Header: STT & Name */}
                  <Box className={classes.mobileCardHeader}>
                    <Box>
                      <Typography variant="subtitle2" style={{ fontWeight: 800, color: "#0F172A" }}>
                        {user?.name}
                      </Typography>
                      <Typography variant="caption" style={{ color: "#64748B" }}>
                        STT: {userList.size * userList.number + index + 1} | Ngày đk: {String(user?.createdAt).slice(8, 10)}-{String(user?.createdAt).slice(5, 7)}-{String(user?.createdAt).slice(0, 4)}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Card Body: Info group */}
                  <Box className={classes.mobileCardBody}>
                    <Box className={classes.mobileCardRow}>
                      <Typography variant="caption" style={{ color: "#64748B", fontWeight: 700 }}>
                        MÃ NHÂN VIÊN
                      </Typography>
                      <Chip
                        label={user?.staffId || "---"}
                        size="small"
                        style={{
                          backgroundColor: "#E2E8F0",
                          color: "#334155",
                          fontWeight: 700,
                          borderRadius: 6,
                        }}
                      />
                    </Box>

                    <Box className={classes.mobileCardRow}>
                      <Typography variant="caption" style={{ color: "#64748B", fontWeight: 700 }}>
                        EMAIL
                      </Typography>
                      <Typography variant="caption" style={{ fontWeight: 600, color: "#334155" }}>
                        {user?.email || "---"}
                      </Typography>
                    </Box>

                    <Box className={classes.mobileCardRow}>
                      <Typography variant="caption" style={{ color: "#64748B", fontWeight: 700 }}>
                        LỚP HỌC
                      </Typography>
                      <Chip
                        label={user?.class?.name || "Chưa xếp lớp"}
                        size="small"
                        style={{
                          backgroundColor: "#E0F2FE",
                          color: "#0369A1",
                          fontWeight: 700,
                          borderRadius: 6,
                        }}
                      />
                    </Box>
                  </Box>

                  {/* Card Actions: Approved & Delete */}
                  <Box className={classes.mobileCardActions} onClick={(e: any) => e.stopPropagation()}>
                    <Button className={classes.btn_detail} onClick={() => handleApproved(user.id)}>
                      Duyệt
                    </Button>
                    <Button className={classes.btn_delete} onClick={() => handleDelete(user)}>
                      Xóa
                    </Button>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </>
      ) : (
        <Box>
          <EmptyComponent isBorder={false} />
        </Box>
      )}

      {userList.totalPages > 1 && (
        <Box className={classes.paginationContainer}>
          <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
            {t("managerStudent.tongcong")}: {userList.totalElements}
          </Typography>
          <Pagination
            count={Math.ceil(userList.totalElements / userList.size)}
            page={userList.number + 1}
            variant="outlined"
            color="primary"
            shape="rounded"
            size={isMobile ? "small" : "medium"}
            siblingCount={isMobile ? 0 : 1}
            onChange={(e, page) => handleChangePage(page)}
          />
        </Box>
      )}

      {/* Registration Detail Dialog Modal */}
      <Modal
        open={openDetailModal}
        onClose={handleCloseDetailModal}
        closeAfterTransition
        style={{ display: "flex", alignItems: "center", justifyContent: "center" }}
      >
        <Slide direction="up" in={openDetailModal}>
          <Box style={{ maxWidth: 600, width: "92%", backgroundColor: "#FFFFFF", borderRadius: 24, overflow: "hidden", border: "1px solid #E2E8F0", boxShadow: "0 25px 50px -12px rgba(15, 23, 42, 0.25)", outline: "none" }}>
            {/* Modal Header */}
            <Box style={{ background: "linear-gradient(135deg, #0F172A 0%, #1E1B4B 50%, #312E81 100%)", color: "#FFFFFF", padding: "18px 24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <AssignmentIndIcon style={{ color: "#818CF8", fontSize: 24 }} />
                <Typography variant="h6" style={{ fontWeight: 800, color: "#FFFFFF", fontSize: "1.125rem" }}>
                  Chi tiết đăng ký
                </Typography>
              </Box>
              <IconButton onClick={handleCloseDetailModal} style={{ color: "rgba(255, 255, 255, 0.7)" }}>
                <CloseIcon />
              </IconButton>
            </Box>

            {/* Modal Content */}
            <Box style={{ padding: "24px 28px" }}>
              {selectedUser && (
                <>
                  <Box style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                    <Box style={{ width: 60, height: 60, borderRadius: 16, backgroundColor: "#EEF2FF", color: "#4F46E5", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, flexShrink: 0 }}>
                      {selectedUser?.name ? selectedUser.name.charAt(0).toUpperCase() : "U"}
                    </Box>
                    <Box>
                      <Typography style={{ fontSize: "1.2rem", fontWeight: 800, color: "#0F172A" }}>
                        {selectedUser?.name}
                      </Typography>
                      <Box style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap" }}>
                        <Chip
                          label={`MNV: ${selectedUser?.staffId || "---"}`}
                          size="small"
                          style={{ backgroundColor: "#F1F5F9", color: "#334155", fontWeight: 700, borderRadius: 6 }}
                        />
                        <Chip
                          label={selectedUser?.class?.name ? `Lớp: ${selectedUser.class.name}` : "Chưa xếp lớp"}
                          size="small"
                          style={{ backgroundColor: "#E0F2FE", color: "#0369A1", fontWeight: 700, borderRadius: 6 }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                        <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Email</Typography>
                        <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser?.email || "Chưa cập nhật"}</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                        <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Số điện thoại</Typography>
                        <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser?.phone || "Chưa cập nhật"}</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                        <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Lớp học đăng ký</Typography>
                        <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser?.class?.name || "Chưa xếp lớp"}</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                        <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Chức vụ</Typography>
                        <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser?.position || "Nhân viên"}</Typography>
                      </Box>
                    </Grid>

                    {selectedUser?.area?.name && (
                      <Grid item xs={12} sm={6}>
                        <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                          <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Khu vực</Typography>
                          <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser.area.name}</Typography>
                        </Box>
                      </Grid>
                    )}

                    {(selectedUser?.department?.title || selectedUser?.department?.name) && (
                      <Grid item xs={12} sm={6}>
                        <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                          <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Bộ phận / Phòng ban</Typography>
                          <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser?.department?.title || selectedUser?.department?.name}</Typography>
                        </Box>
                      </Grid>
                    )}

                    {(selectedUser?.newProvince?.name || selectedUser?.province?.name) && (
                      <Grid item xs={12} sm={6}>
                        <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                          <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Tỉnh / Thành phố</Typography>
                          <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser?.newProvince?.name || selectedUser?.province?.name}</Typography>
                        </Box>
                      </Grid>
                    )}

                    {selectedUser?.workType?.name && (
                      <Grid item xs={12} sm={6}>
                        <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                          <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Hình thức làm việc</Typography>
                          <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>{selectedUser.workType.name}</Typography>
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12} sm={6}>
                      <Box style={{ backgroundColor: "#F8FAFC", borderRadius: 12, padding: "12px 16px", border: "1px solid #F1F5F9" }}>
                        <Typography style={{ fontSize: "0.75rem", fontWeight: 700, color: "#64748B", textTransform: "uppercase" }}>Ngày đăng ký</Typography>
                        <Typography style={{ fontSize: "0.9rem", fontWeight: 700, color: "#0F172A", marginTop: 2 }}>
                          {selectedUser?.createdAt
                            ? `${dateTimeToFormatDDMMYYYY(selectedUser.createdAt)} ${dateTimeToFormatHHMM(selectedUser.createdAt)}`
                            : "---"}
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </>
              )}
            </Box>

            {/* Modal Actions */}
            <Box style={{ padding: "16px 28px", backgroundColor: "#F8FAFC", borderTop: "1px solid #E2E8F0", display: "flex", justifyContent: "flex-end", gap: 12 }}>
              <Button
                variant="outlined"
                onClick={handleCloseDetailModal}
                style={{ borderRadius: 10, padding: "8px 20px", fontWeight: 700, textTransform: "none", color: "#475569", borderColor: "#CBD5E1" }}
              >
                Đóng
              </Button>
              {selectedUser && (
                <Button
                  className={classes.btn_detail}
                  onClick={() => {
                    handleApproved(selectedUser.id);
                    handleCloseDetailModal();
                  }}
                  style={{ margin: 0, padding: "8px 24px", fontSize: "0.875rem" }}
                >
                  Duyệt đăng ký
                </Button>
              )}
            </Box>
          </Box>
        </Slide>
      </Modal>
    </div>
  );
}

